import { ThunkAction } from 'redux-thunk';
import { fetchSelectionImages } from 'store/slices/images';
import { saveSelection } from 'store/slices/selection';
import { hydrateNewSelection } from 'modules/ScrAppBookNew/store/selection';
import { saveEndCustomer, IEndCustomerState } from 'store/slices/endCustomer';
import { setCookie, getCookie } from 'helpers/cookie';
import { setGalleries } from 'store/slices/collection';
import { getErrorStatus } from 'helpers/network';
import { getSelectionIdFromUrl } from 'helpers/selectionUrl';
import axios from '../utils/axios';

import { dispatchCurrentShopAction } from 'old-store/utils';
import { toggleLoginWithPasswordModalAction } from 'modules/ScrShopNew/store/slices/shop';
import { toggleLoginWithPasswordModalAction as oldToggleLoginWithPasswordModalAction } from 'modules/ScrShop/store/actions';

import { ISelection, IGallery } from 'old-store/types/gallery';
import { RootState } from 'old-store/store';

type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, any>;

interface IEndCustomerResponse extends Partial<IEndCustomerState> {
  type?: string;
  selections: ISelection[];
  allUserSelections?: ISelection[];
  collectionGalleries?: IGallery[];
}

interface IGetEndCustomerProps {
  includeAllUserSelections?: boolean;
}

interface ICreateEndCustomerData {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  telephoneNumber?: string;
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  _country?: string;
  marketingEmailsEnabled?: boolean;
  [key: string]: any;
}

interface ILoginEndCustomerData {
  email: string;
  password: string;
  [key: string]: any;
}

interface IApiResponse<T = any> {
  statusCode: number;
  data?: T;
}

interface IToggleModalPayload {
  [key: string]: any;
}

export const getEndCustomer =
  (props?: IGetEndCustomerProps): AppThunk<Promise<IEndCustomerResponse | null>> =>
  async (dispatch, getState) => {
    try {
      const { includeAllUserSelections = false } = props || {};
      const { collection } = getState();

      const persistData = localStorage.getItem('endCustomerToken');
      const endCustomerTokenMap = persistData ? JSON.parse(persistData) : {};
      const currentToken = endCustomerTokenMap[collection._user];
      if (currentToken) setCookie('endCustomerToken', currentToken, 30);

      const res = await axios.get<IEndCustomerResponse>('/api/collection/endCustomer', {
        params: {
          collection_id: collection?._id || window.SITE_ID,
          includeAllUserSelections,
          selectionId: getSelectionIdFromUrl()
        }
      });

      if (!currentToken) {
        // no token in local storage yet we need to add it so that subsequent request and check for temp end customer do not fail
        // since the get end customer request (above) fails if the user from the current collection do not fit with the end customer from the cookie
        // that is used we know that the current cookie is from the current collection user

        localStorage.setItem(
          'endCustomerToken',
          JSON.stringify({ [collection._user]: getCookie('endCustomerToken') })
        );
      }

      const isUserAuthed = !!(res.data.type && res.data.type === 'permanent');
      const isUserPreAuthed = !!(res.data.type && res.data.type === 'fly');

      dispatch(saveEndCustomer({ ...res.data, authed: isUserAuthed, preAuthed: isUserPreAuthed }));

      dispatch(fetchSelectionImages(res.data.selections));
      const selectionPayload = {
        selections: res.data.selections,
        allUserSelections: res.data.allUserSelections || []
      };
      const usesNewGallery = collection.collectionCreator?.featureFlags?.newDesign === true;

      dispatch(
        usesNewGallery ? hydrateNewSelection(selectionPayload) : saveSelection(selectionPayload)
      );
      if (res.data.collectionGalleries) {
        dispatch(setGalleries(res.data.collectionGalleries));
      }

      dispatch({
        type: 'set_selection_fetch_status',
        payload: 'success'
      });

      return res.data;
    } catch (e) {
      dispatch({
        type: 'set_selection_fetch_status',
        payload: 'error'
      });
      dispatch({ type: 'set_endCustomer_auth', payload: false });
      dispatch({ type: 'set_endCustomer_pre_auth', payload: false });

      return null;
    }
  };

export const createEndCustomer =
  (data: ICreateEndCustomerData): AppThunk<Promise<IApiResponse>> =>
  async () => {
    try {
      const res = await axios.put<any>('/api/collection/endCustomer', {
        ...data,
        collection_id: window.SITE_ID
      });

      return { statusCode: 200, data: res.data };
    } catch (e: any) {
      console.log(e);

      return { statusCode: getErrorStatus(e) ?? 500 };
    }
  };

export const loginEndCustomer =
  (data: ILoginEndCustomerData): AppThunk<Promise<IApiResponse>> =>
  async () => {
    try {
      const res = await axios.post<any>('/api/collection/endCustomer/login', {
        ...data,
        collection_id: window.SITE_ID
      });

      return { statusCode: 200, data: res.data };
    } catch (e: any) {
      return { statusCode: getErrorStatus(e) ?? 500 };
    }
  };

export const toggleCreateEndCustomerModal =
  (payload: IToggleModalPayload = {}): AppThunk =>
  (dispatch) =>
    dispatch({ type: 'toggle_create_end_customer_modal', payload });

export const toggleLoginEndCustomerModal =
  (payload: IToggleModalPayload = {}): AppThunk =>
  (dispatch) => {
    dispatchCurrentShopAction(dispatch, payload)(
      toggleLoginWithPasswordModalAction,
      oldToggleLoginWithPasswordModalAction
    );
  };
